/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get, isUndefined, isString, isObject, merge } from 'lodash';
import { bacsHmrcReferenceMaxLength, addPayeeFormLengths, swiftRegex, swiftRegexMultiChar } from '../config/';

export const isNumeric = (value) => value && /^(0\.\d)?(\d*(\.\d{1,4})?)$/.test(value);
export const isValidCurrencyToEnter = (value) => /^(\d*(\.(\d{1,2})?)?)$/.test(value);
export const isName = (value) => /[\w\s&()/\\-_.]+/.test(value);
export const swift = (value) => swiftRegex.test(value);
export const swiftMultiChar = (value) => swiftRegexMultiChar.test(value);
export const alphaNumeric = (value) => /[0-9A-Za-z]/.test(value);
export const isPayeeName = (value) => {
	const nameRegx = /[^0-9A-Za-z\s&'().,/-]/g;
	const isPayeeNameValid = value.match(nameRegx);
	if (isPayeeNameValid !== null) {
		return isPayeeNameValid.length === 0;
	}
	return true;
};

const removeCommas = (amount = '') => amount.replace(/,/g, '');

const { sortCode, accountNumber } = addPayeeFormLengths || {};
export const validationRules = {
	required: ({ value }) => value && value.length,
	amount: ({ value }) => isNumeric(removeCommas(value)) && parseFloat(value) > 0,
	date: ({ value }) => moment.isMoment(value),
	minDate: ({ value, type }) => {
		const minDate = type === 'single' || !type ? moment() : moment().add(1, 'day');
		return moment.isMoment(value) && moment(value).isSameOrAfter(minDate.startOf('day'));
	},
	reference: ({ value, isToPayee, optionalReference, chapsMode, toPayees, toAccount }) => {
		if (chapsMode) {
			const toPayee = toPayees.find(({ id }) => id === toAccount);
			if (toPayee?.isCopCheckRequired && value === '') {
				return false;
			}
			return true;
		}
		if (optionalReference) return true;
		return isToPayee ? value && value.length : true;
	},
	referenceContent: ({ value, isToPayee, chapsMode, isToPayeeCopCheckRequired }) => {
		if (chapsMode) {
			return swiftMultiChar(value);
		} else if (!isToPayeeCopCheckRequired) return true;
		return isToPayee ? isPayeeName(value) : true;
	},
	iftReference: ({ value }) => swiftMultiChar(value),
	hmrcReference: ({ value }) => !value || value.length === Number(bacsHmrcReferenceMaxLength),
	maximum: ({ value, maximum }) => Number(value) <= maximum,
	name: ({ value }) => isName(value),
	sortCode: ({ value }) => value && value.replace(/-/g, '').length === sortCode,
	accountNumber: ({ value }) => value.length === accountNumber,
	swift: ({ value }) => swift(value),
	swiftMultiChar: ({ value }) => swiftMultiChar(value),
	alphaNumeric: ({ value }) => alphaNumeric(value),
	chapsMaxLength: ({ value, chapsMaxLength, chapsPaymentsEnable }) => {
		if (chapsPaymentsEnable) return value.length <= chapsMaxLength;
		return true;
	},
	maxLength: ({ value, maxLength }) => !isUndefined(value) && String(value).length <= maxLength,
	minLength: ({ value, minLength }) => !isUndefined(value) && String(value).length >= minLength,
	checked: ({ value }) => typeof value === 'boolean' && value,
	bicLength: ({ value, maxLength, minLength }) =>
		!isUndefined(value) && (String(value).length === maxLength || String(value).length === minLength),
	isPayeeName: ({ value }) => isPayeeName(value),
};

export default (options) => (WrappedComponent) => {
	const withValidation = class extends Component {
		constructor(props) {
			super(props);
			this.state = {
				validationRules: options.validations.map((ruleKey) => ({
					evaluate: validationRules[ruleKey],
					message: options.message,
					key: ruleKey,
				})),
			};

			this.onFocus = this.onFocus.bind(this);
			this.onBlur = this.onBlur.bind(this);
			this.onChange = this.onChange.bind(this);
		}

		componentDidMount() {
			const value = this.props[options.key];
			const isValid = this.isValid(value, this.props, this.state);
			this.props.updateValidation(options.key, isValid);
			/* eslint-disable react/no-did-mount-set-state */
			this.setState({
				validationRules: merge(this.state.validationRules, this.props.validationRules),
			});
			/* eslint-enable react/no-did-mount-set-state */
		}

		componentWillUpdate(nextProps, nextState) {
			if (
				this.props[options.key] !== nextProps[options.key] ||
				this.state.validationRules !== nextState.validationRules ||
				this.props.useValidation !== nextProps.useValidation
			) {
				const value = nextProps[options.key];
				const isValid = this.isValid(value, nextProps, nextState);
				this.props.updateValidation(options.key, isValid);
			}
		}

		onBlur(event) {
			const { validations, updateValidation, onBlur } = this.props;
			const validationBlock = validations[options.key] || {};
			const { showValidation = false } = validationBlock;
			const isValid = this.isValid(this.props[options.key], this.props, this.state);
			if (showValidation) {
				updateValidation(options.key, isValid, false, false, true);
			} else updateValidation(options.key, isValid);
			onBlur(event);
		}

		onFocus(event) {
			const { onFocusCheck } = this.props;
			if (onFocusCheck) {
				this.props.hideErrorMsg(options.key);
			}
			this.props.onFocus(event);
		}

		onChange(event) {
			this.props.hideErrorMsg(options.key);
			const value = get(event, 'target.value', event);

			const isValid = this.isValid(value, this.props, this.state);
			this.props.updateValidation(options.key, isValid);
			this.props.onChange(event);
		}
		isValid(value, props, state) {
			if (get(props, 'useValidation', true)) {
				return state.validationRules.every((rule) => rule.evaluate({ value, ...props }));
			}
			return true;
		}

		render() {
			const validationBlock = this.props.validations[options.key] || {};
			const { showValidation = false } = validationBlock;

			const isValid = this.props.useValidation ? get(validationBlock, 'isValid', false) : true;
			const isFocusOn = this.props.useValidation && (validationBlock.isFocusOn || false);
			const getErrorMessage = () => {
				const value = this.props[options.key];
				const rules = this.state.validationRules;
				const failedRule = rules.find((rule) => !rule.evaluate({ value, ...this.props }));
				if (!failedRule) return '';
				if (isObject(failedRule.message)) {
					return failedRule.message[failedRule.key];
				}
				return isString(failedRule.message) ? failedRule.message : '';
			};

			const errorMessage = !isValid && !!showValidation ? getErrorMessage() : null;

			return (
				<WrappedComponent
					{...{
						...this.props,
						onFocus: this.onFocus,
						onBlur: this.onBlur,
						onChange: this.onChange,
						showValidation,
						isValid,
						isFocusOn,
						errorMessage,
					}}
				/>
			);
		}
	};

	withValidation.defaultProps = {
		useValidation: true,
		validationRules: [],
		onChange: () => {},
		onBlur: () => {},
		onFocus: () => {},
		hideErrorMsg: () => {},
		onFocusCheck: true,
	};

	withValidation.propTypes = {
		updateValidation: PropTypes.func.isRequired,
		hideErrorMsg: PropTypes.func,
		validations: PropTypes.shape({}).isRequired,
		onFocus: PropTypes.func,
		onBlur: PropTypes.func,
		useValidation: PropTypes.bool,
		validationRules: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.string.isRequired,
				evaluate: PropTypes.func.isRequired,
				message: PropTypes.string.isRequired,
			})
		),
		onChange: PropTypes.func,
		onFocusCheck: PropTypes.bool,
	};

	withValidation.displayName = `WithValidation(${
		WrappedComponent.displayName || WrappedComponent.name || 'Component'
	})`;

	return withValidation;
};
